import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { canMatchAuthGuard } from '@interacta-shared/data-access-auth';
import { PageView } from '@interacta-shared/data-access-common';
import {
    licenceRequired,
    licenseUpgradeGuard,
} from '@interacta-shared/data-access-license';
import { featureErrorRoutes } from '@interacta-shared/feature-error';
import { featureLicenseRoutes } from '@interacta-shared/feature-license';
import { dynamicImport, SKIP_TITLE } from '@interacta-shared/util-common';
import { CapabilitiesGuard } from '@modules/app-routing';
import { EnvironmentInfoGuard } from './guards/environment-info.guard';
import { EnvironmentGuard } from './guards/environment.guard';
import { UserSettingsGuard } from './guards/user-settings.guard';
import {
    bulkWorkflowTransition,
    communityDetail,
    customerFeedback,
    digitalWorkplace,
    iLab,
    insights,
    login,
    manage,
    notificationSettings,
    openSourceLicenses,
    people,
    post,
    postCopy,
    postCreate,
    postEdit,
    postFeedback,
    postILab,
    postPrint,
    postRelations,
    profile,
    search,
    unsubscribe,
} from './routes';

const appRoutes: Routes = [
    {
        path: 'external-auth-redirect/:resultToken',
        loadComponent: () =>
            dynamicImport(() => import('@interacta-shared/feature-login')).then(
                (m) => m.ExternalAuthRedirectPageComponent,
            ),
    },
    {
        path: 'error',
        children: featureErrorRoutes,
    },
    {
        path: licenceRequired,
        children: featureLicenseRoutes(),
    },
    {
        path: unsubscribe,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../unsubscribe')).then(
                (m) => m.unsubscribeRoutes,
            ),
    },
    {
        path: login,
        loadChildren: () =>
            dynamicImport(() => import('@interacta-shared/feature-login')).then(
                (m) => m.featureLoginRoutes,
            ),
    },
    {
        path: people,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../people')).then(
                (m) => m.featurePeopleRoutes,
            ),
    },
    {
        path: manage,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../manage/manage.routes')).then(
                (m) => m.manageRoutes,
            ),
    },
    {
        path: 'digital-workplace-admin',
        canMatch: [canMatchAuthGuard],
        canActivate: [
            licenseUpgradeGuard(
                'MANAGE_DIGITAL_WORKPLACE',
                undefined,
                'DIGITAL_WORKPLACE.ADMIN.HOME_LIST.TITLE',
            ),
        ],
        loadChildren: () =>
            dynamicImport(
                () =>
                    import('../digital-workplace-admin/digital-workplace-admin.module'),
            ).then((m) => m.DigitalWorkplaceAdminModule),
    },
    {
        path: digitalWorkplace,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () => import('../digital-workplace/digital-workplace.module'),
            ).then((m) => m.DigitalWorkplaceModule),
    },
    {
        path: insights,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../insights/insights.routes')).then(
                (m) => m.insightsRoutes,
            ),
    },
    {
        path: 'bootcamp',
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../bootcamp/bootcamp.module')).then(
                (m) => m.BootcampModule,
            ),
    },
    {
        path: search,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../search')).then(
                (m) => m.featureSearchRoutes,
            ),
    },
    {
        path: postCreate,
        canMatch: [canMatchAuthGuard],
        data: {
            id: 'Create Post' satisfies PageView,
            mode: 'create',
            title: SKIP_TITLE,
        },
        loadChildren: () =>
            dynamicImport(
                () => import('../post-create/post-create.module'),
            ).then((m) => m.PostCreateModule),
    },
    {
        path: postEdit,
        canMatch: [canMatchAuthGuard],
        data: {
            id: 'Edit Post' satisfies PageView,
            mode: 'edit',
            title: SKIP_TITLE,
        },
        loadChildren: () =>
            dynamicImport(
                () => import('../post-create/post-create.module'),
            ).then((m) => m.PostCreateModule),
    },
    {
        path: postCopy,
        canMatch: [canMatchAuthGuard],
        data: {
            id: 'Copy Post' satisfies PageView,
            mode: 'copy',
            title: SKIP_TITLE,
        },
        loadChildren: () =>
            dynamicImport(
                () => import('../post-create/post-create.module'),
            ).then((m) => m.PostCreateModule),
    },
    {
        path: postPrint,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../post-print')).then(
                (m) => m.featurePostPrintRoutes,
            ),
    },
    {
        path: postFeedback,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../feedback/feedback.routes')).then(
                (m) => m.feedbackRoutes,
            ),
    },
    {
        path: postILab,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../post-i-lab')).then(
                (m) => m.featurePostILabRoutes,
            ),
    },
    {
        path: postRelations,
        canMatch: [canMatchAuthGuard],
        data: { title: SKIP_TITLE },
        loadChildren: () =>
            dynamicImport(() => import('../post-relations')).then(
                (m) => m.featurePostRelationsRoutes,
            ),
    },
    {
        path: post,
        canMatch: [canMatchAuthGuard],
        data: { title: SKIP_TITLE },
        loadChildren: () =>
            dynamicImport(
                () => import('../post-detail/post-detail.module'),
            ).then((m) => m.PostDetailModule),
    },
    {
        path: profile,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../profile')).then(
                (m) => m.featureProfileRoutes,
            ),
    },
    {
        path: communityDetail,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () => import('../community-detail/community-detail.routes'),
            ).then((m) => m.communityDetailRoutes),
    },
    {
        path: notificationSettings,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () =>
                    import('../notification-settings/notification-settings.routes'),
            ).then((m) => m.notificationSettingsRoutes),
    },
    {
        path: openSourceLicenses,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () =>
                    import('../open-source-licenses/open-source-licenses.routes'),
            ).then((m) => m.openSourceLicensesRoutes),
    },
    {
        path: bulkWorkflowTransition,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () =>
                    import('@modules/bulk-workflow-transition/bulk-workflow-transition.routes'),
            ).then((m) => m.bulkWorkflowTransitionRoutes),
    },
    {
        path: customerFeedback,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(
                () => import('@interacta/feature-customer-feedback'),
            ).then((m) => m.customerFeedbackRoutes),
    },
    {
        path: iLab,
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('@interacta/feature-i-lab')).then(
                (m) => m.featureILabRoutes,
            ),
    },
    {
        path: '',
        canMatch: [canMatchAuthGuard],
        loadChildren: () =>
            dynamicImport(() => import('../dashboard/dashboard.module')).then(
                (m) => m.DashboardModule,
            ),
    },
    {
        path: '**',
        redirectTo: 'error/page-not-found',
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(appRoutes, {
            paramsInheritanceStrategy: 'emptyOnly',
            // enableTracing: true,
            // preloadingStrategy: PreloadAllModules,
        }),
    ],
    declarations: [],
    providers: [
        CapabilitiesGuard,
        UserSettingsGuard,
        EnvironmentGuard,
        EnvironmentInfoGuard,
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
